import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CalendarClock, Loader2, ExternalLink, Settings, Unlink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isAgent } from '@/lib/utils';
import { googleCalendarAPI } from '@/lib/api';

/**
 * Google Calendar connection prompt for Leads + Events pages.
 *
 * Behavior contract:
 *   - Only rendered for Agent-role users.
 *   - Only opens if the agent is NOT connected AND the prompt has not
 *     been dismissed in the current session AND (never dismissed OR
 *     last dismissed > 7 days ago).
 *   - "Not now" closes the modal and marks it dismissed for the
 *     session; also records a persistent 7-day cooldown in
 *     localStorage.
 *   - "Connect Google Calendar" kicks the OAuth flow (window redirect
 *     — Google lands us back on /profile?google=connected which the
 *     GoogleCalendarIntegration component picks up via query param).
 *   - When integration is temporarily unavailable (auth-url endpoint
 *     500s) we silently skip the prompt for this session.
 */

const SESSION_KEY = 'gcal_prompt_dismissed_session';
const PERSISTENT_KEY = 'gcal_prompt_dismissed_at';
const CONNECTING_KEY = 'gcal_prompt_connecting';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function shouldShowPrompt(status) {
  if (!status || status.connected) return false;
  if (sessionStorage.getItem(SESSION_KEY) === '1') return false;
  if (sessionStorage.getItem(CONNECTING_KEY) === '1') return false;
  const lastDismissed = localStorage.getItem(PERSISTENT_KEY);
  if (lastDismissed) {
    const at = Date.parse(lastDismissed);
    if (!Number.isNaN(at) && Date.now() - at < COOLDOWN_MS) return false;
  }
  return true;
}

export function GoogleCalendarConnectPrompt({ context = 'leads' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const eligible = isAgent(user);

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await googleCalendarAPI.status();
        if (cancelled) return;
        setStatus(res.data);
        if (shouldShowPrompt(res.data)) setOpen(true);
      } catch {
        // Integration temporarily unavailable — skip prompt this session.
      }
    })();
    return () => { cancelled = true; };
  }, [eligible]);

  const handleNotNow = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, '1');
    localStorage.setItem(PERSISTENT_KEY, new Date().toISOString());
    setOpen(false);
  }, []);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    sessionStorage.setItem(CONNECTING_KEY, '1');
    try {
      const returnTo = window.location.pathname + window.location.search;
      const res = await googleCalendarAPI.getAuthUrl(returnTo);
      window.location.href = res.data.authorization_url;
    } catch {
      sessionStorage.removeItem(CONNECTING_KEY);
      toast.error('Could not start the Google connection. Try again from your profile.');
      setConnecting(false);
      setOpen(false);
    }
  }, []);

  if (!eligible) return null;

  const contextCopy = context === 'events'
    ? 'your events, appointments, and follow-ups'
    : 'your follow-ups and lead appointments';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleNotNow(); }}>
      <DialogContent className="sm:max-w-md" data-testid="gcal-connect-prompt">
        <DialogHeader>
          <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
            <CalendarClock size={24} className="text-blue-600" />
          </div>
          <DialogTitle className="text-center">Connect your Google Calendar</DialogTitle>
          <DialogDescription className="text-center pt-2">
            Sync {contextCopy} from KR Agency Hub to your personal Google Calendar.
            You&apos;ll get an automatic reminder 1 hour before each event.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 mt-2">
          <p className="font-medium text-slate-700 mb-1">One-way sync</p>
          <p>
            Changes you make in KR Agency Hub are reflected on Google Calendar automatically.
            Changes made directly in Google Calendar <span className="font-medium">do not</span>{' '}
            flow back to KR Agency Hub.
          </p>
        </div>
        <DialogFooter className="sm:justify-between gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={handleNotNow}
            data-testid="gcal-prompt-not-now"
          >
            Not now
          </Button>
          <Button
            onClick={handleConnect}
            disabled={connecting}
            data-testid="gcal-prompt-connect"
            className="bg-[#4285F4] hover:bg-[#357ae8] text-white"
          >
            {connecting ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Redirecting...</>
            ) : (
              <><ExternalLink size={14} className="mr-2" /> Connect Google Calendar</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConnectGCalChip() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const eligible = isAgent(user);

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await googleCalendarAPI.status();
        if (!cancelled) setStatus(res.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [eligible]);

  // Toast + refresh status once Google bounces the user back to any
  // page with `?google=connected`. We keep the query param removal
  // localized so we don't disturb other query state.
  useEffect(() => {
    if (!eligible) return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('google');
    if (!flag) return;
    if (flag === 'connected') {
      toast.success('Google Calendar connected');
      (async () => {
        try {
          const res = await googleCalendarAPI.status();
          setStatus(res.data);
        } catch { /* silent */ }
        // Refresh the current user so the freshly-imported Google
        // profile avatar shows up in the sidebar/header immediately.
        try { await refreshUser?.(); } catch { /* silent */ }
      })();
    } else if (flag === 'error') {
      const reason = params.get('reason') || 'unknown';
      const detail = params.get('detail');
      toast.error(
        detail
          ? `Could not connect Google Calendar: ${reason.replace(/_/g, ' ')} — ${detail}`
          : `Could not connect Google Calendar: ${reason.replace(/_/g, ' ')}`,
        { duration: 15000 },
      );
    }
    params.delete('google'); params.delete('reason'); params.delete('detail');
    const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.replaceState({}, '', clean);
  }, [eligible]);

  if (!eligible || !status) return null;

  // Phase 27.30.33 — Explicit "Connected" chip so the agent sees at a
  // glance whether their calendar is linked. Phase 27.50.B.16 —
  // clicking the chip opens a popover with "Disconnect" and "Settings"
  // actions so users don't have to hunt for the disconnect button on
  // /profile.
  if (status.connected) {
    return (
      <ConnectedChipPopover
        status={status}
        onDisconnected={() => setStatus({ ...status, connected: false, google_email: null })}
      />
    );
  }

  const handleClick = async () => {
    setConnecting(true);
    sessionStorage.setItem(CONNECTING_KEY, '1');
    try {
      const returnTo = window.location.pathname + window.location.search;
      const res = await googleCalendarAPI.getAuthUrl(returnTo);
      window.location.href = res.data.authorization_url;
    } catch {
      sessionStorage.removeItem(CONNECTING_KEY);
      toast.error('Could not start the Google connection.');
      setConnecting(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={connecting}
      data-testid="connect-gcal-chip"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-60"
    >
      {connecting ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <CalendarClock size={12} />
      )}
      Connect Google Calendar
    </button>
  );
}


/**
 * Popover shown when the user clicks the green "Google Calendar
 * connected" chip. Surfaces the linked account, a shortcut to the
 * full integration card on /profile, and a Disconnect action so users
 * can unlink without hunting through the profile page.
 */
function ConnectedChipPopover({ status, onDisconnected }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await googleCalendarAPI.disconnect();
      toast.success('Google Calendar disconnected');
      setOpen(false);
      setConfirming(false);
      onDisconnected?.();
    } catch {
      toast.error('Could not disconnect Google Calendar. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirming(false); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="gcal-connected-chip"
          title={status.google_email ? `Connected as ${status.google_email}` : 'Google Calendar connected'}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors cursor-pointer"
        >
          <CalendarClock size={12} />
          Google Calendar connected
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="gcal-connected-popover">
        <div className="p-3 border-b border-slate-100">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
            Google Calendar
          </p>
          <p className="text-xs text-slate-700 flex items-center gap-1.5">
            <CalendarClock size={12} className="text-emerald-600" />
            <span className="truncate">
              {status.google_email || 'Linked'}
            </span>
          </p>
        </div>
        {!confirming ? (
          <div className="p-2 flex flex-col gap-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 text-xs text-slate-700 hover:bg-slate-50 rounded px-2 py-2"
              data-testid="gcal-chip-settings-link"
            >
              <Settings size={13} className="text-slate-500" />
              View integration settings
            </Link>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="flex items-center gap-2 text-xs text-rose-700 hover:bg-rose-50 rounded px-2 py-2 text-left"
              data-testid="gcal-chip-disconnect-btn"
            >
              <Unlink size={13} />
              Disconnect Google Calendar
            </button>
          </div>
        ) : (
          <div className="p-3 space-y-3">
            <p className="text-xs text-slate-700">
              Disconnect your Google Calendar? Events already synced will
              remain on your calendar, but new items won&apos;t sync until
              you reconnect.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirming(false)}
                disabled={disconnecting}
                data-testid="gcal-chip-cancel-disconnect"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDisconnect}
                disabled={disconnecting}
                data-testid="gcal-chip-confirm-disconnect"
              >
                {disconnecting ? (
                  <><Loader2 size={12} className="mr-1.5 animate-spin" /> Disconnecting…</>
                ) : (
                  <><Unlink size={12} className="mr-1.5" /> Disconnect</>
                )}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
